import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

const EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

interface MediaContextMenuProps {
  x: number;
  y: number;
  isStarred?: boolean;
  isPinned?: boolean;
  onClose: () => void;
  onReply: () => void;
  onCopy: () => void;
  onReact: (emoji: string) => void;
  onDownload: () => void;
  onForward: () => void;
  onPin: () => void;
  onStar: () => void;
  onReport: () => void;
  onDelete: () => void;
}

const MediaContextMenu = ({
  x,
  y,
  isStarred,
  isPinned,
  onClose,
  onReply,
  onCopy,
  onReact,
  onDownload,
  onForward,
  onPin,
  onStar,
  onReport,
  onDelete,
}: MediaContextMenuProps) => {
  const menuRef = useRef<HTMLMenuElement>(null);
  const [measured, setMeasured] = useState(false);

  const style = useMemo(() => {
    const approximateWidth = 180;
    const approximateHeight = 300;
    let left = x;
    let top = y;

    if (typeof window !== 'undefined') {
      const padding = 8;
      const maxRight = window.innerWidth - approximateWidth - padding;
      const maxBottom = window.innerHeight - approximateHeight - padding;
      if (left > maxRight) left = maxRight;
      if (left < padding) left = padding;
      if (top > maxBottom) top = maxBottom;
      if (top < padding) top = padding;
    }

    return { top, left };
  }, [x, y]);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose]);

  useLayoutEffect(() => {
    // After the first render, remeasure the real menu size so we can clamp it
    // against the viewport. This prevents overflow on small mobile screens.
    if (!menuRef.current || measured) return;
    const rect = menuRef.current.getBoundingClientRect();
    const padding = 8;
    const maxRight = window.innerWidth - rect.width - padding;
    const maxBottom = window.innerHeight - rect.height - padding;
    let left = x;
    let top = y;
    if (left > maxRight) left = maxRight;
    if (left < padding) left = padding;
    if (top > maxBottom) top = maxBottom;
    if (top < padding) top = padding;
    if (left !== x || top !== y) {
      menuRef.current.style.left = `${left}px`;
      menuRef.current.style.top = `${top}px`;
    }
    setMeasured(true);
  }, [measured, x, y]);

  return (
    <menu
      ref={menuRef}
      className="media-context-menu"
      style={style}
      onClick={(event) => event.stopPropagation()}
      aria-label="Media-opties menu"
    >
      <li>
        <button type="button" onClick={onReply}>
          Beantwoorden
        </button>
      </li>
      <li>
        <button type="button" onClick={onCopy}>
          Kopiëren
        </button>
      </li>
      <li className="media-context-emoji-bar" role="group" aria-label="Reageren">
        {EMOJIS.map((emoji) => (
          <button
            key={emoji}
            type="button"
            className="media-context-emoji"
            aria-label={`Reageer met ${emoji}`}
            onClick={() => onReact(emoji)}
          >
            {emoji}
          </button>
        ))}
        <button
          type="button"
          className="media-context-emoji"
          aria-label="Meer emoji's"
          onClick={() => {
            const choice = window.prompt('Kies een emoji om mee te reageren:');
            if (choice) onReact(choice);
          }}
        >
          +
        </button>
      </li>
      <li>
        <button type="button" onClick={onDownload}>
          Downloaden
        </button>
      </li>
      <li>
        <button type="button" onClick={onForward}>
          Doorsturen
        </button>
      </li>
      <li>
        <button type="button" onClick={onPin}>
          {isPinned ? 'Losmaken' : 'Vastzetten'}
        </button>
      </li>
      <li>
        <button type="button" onClick={onStar}>
          {isStarred ? 'Verwijder ster' : 'Met ster markeren'}
        </button>
      </li>
      <li>
        <button type="button" onClick={onReport}>
          Rapporteren
        </button>
      </li>
      <li>
        <button type="button" className="danger" onClick={onDelete}>
          Verwijderen
        </button>
      </li>
    </menu>
  );
};

export default MediaContextMenu;
